import { useCallback, useEffect, useMemo, useState } from "react";
import type { RefObject } from "react";

import { SelectChangeEvent } from "@mui/material/Select";

import {
  ALL_LABEL,
  ALL_VALUE,
  BADGE_GAP,
  BADGE_WIDTH,
  EMPTY_LABEL,
  RESERVED_WIDTH,
} from "./styles";

import type { Option, VisibleResult } from "./types";

import { capitalizeFirstLetterFullString as capitalizeWords } from "@utils/index";

const createTextMeasurer = () => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (context) {
    context.font = "400 16px Montserrat, Arial";
  }

  return (text: string) => {
    if (!context) {
      return text.length * 8;
    }

    return context.measureText(text).width;
  };
};

export const useElementWidth = (ref: RefObject<HTMLElement | null>) => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    const updateWidth = () => {
      setWidth(element.clientWidth);
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);

    return () => observer.disconnect();
  }, [ref]);

  return width;
};

const getVisibleLabels = (
  labels: string[],
  width: number,
  measureText: (text: string) => number
): VisibleResult => {
  const availableWidth = width - RESERVED_WIDTH;

  if (!labels.length) {
    return { text: EMPTY_LABEL, hiddenCount: 0 };
  }

  if (availableWidth <= 0) {
    return {
      text: labels[0],
      hiddenCount: Math.max(labels.length - 1, 0),
    };
  }

  let visibleLabels: string[] = [];

  for (let index = 0; index < labels.length; index += 1) {
    const nextVisibleLabels = [...visibleLabels, labels[index]];
    const hiddenCount = labels.length - nextVisibleLabels.length;
    const badgeSpace = hiddenCount > 0 ? BADGE_WIDTH + BADGE_GAP : 0;
    const maxTextWidth = availableWidth - badgeSpace;
    const candidateText = nextVisibleLabels.join(", ");

    if (measureText(candidateText) <= maxTextWidth) {
      visibleLabels = nextVisibleLabels;
      continue;
    }

    if (!visibleLabels.length) {
      return {
        text: labels[0],
        hiddenCount: Math.max(labels.length - 1, 0),
      };
    }

    return {
      text: visibleLabels.join(", "),
      hiddenCount: labels.length - visibleLabels.length,
    };
  }

  return {
    text: visibleLabels.join(", "),
    hiddenCount: 0,
  };
};

export const useDropdownSelectAll = (
  options: Option[],
  value: string[],
  onChange: (values: string[]) => void
) => {
  const [selectedValues, setSelectedValues] = useState<string[]>(value);
  const measureText = useMemo(() => createTextMeasurer(), []);

  useEffect(() => {
    setSelectedValues(value);
  }, [value]);

  const optionValues = useMemo(() => options.map((option) => option.value), [
    options,
  ]);

  const allSelected = useMemo(
    () =>
      options.length > 0 &&
      optionValues.every((optionValue) => selectedValues.includes(optionValue)),
    [options.length, optionValues, selectedValues]
  );

  const someSelected = useMemo(
    () => selectedValues.length > 0 && !allSelected,
    [selectedValues.length, allSelected]
  );

  const selectedLabels = useMemo(
    () =>
      options
        .filter((option) => selectedValues.includes(option.value))
        .map((option) => capitalizeWords(option.label)),
    [options, selectedValues]
  );

  const updateValues = useCallback(
    (newValues: string[]) => {
      setSelectedValues(newValues);
      onChange(newValues);
    },
    [onChange]
  );

  const toggleAll = useCallback(() => {
    updateValues(allSelected ? [] : optionValues);
  }, [allSelected, optionValues, updateValues]);

  const handleChange = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const rawValues = event.target.value as string[];

      if (rawValues.includes(ALL_VALUE)) {
        toggleAll();
        return;
      }

      updateValues(rawValues.filter((item) => item !== ALL_VALUE));
    },
    [toggleAll, updateValues]
  );

  const getRenderValue = useCallback(
    (width: number): VisibleResult => {
      if (!selectedLabels.length) {
        return { text: EMPTY_LABEL, hiddenCount: 0 };
      }

      if (allSelected) {
        return { text: ALL_LABEL, hiddenCount: 0 };
      }

      return getVisibleLabels(selectedLabels, width, measureText);
    },
    [allSelected, measureText, selectedLabels]
  );

  return {
    selectedValues,
    allSelected,
    someSelected,
    handleChange,
    getRenderValue,
  };
};
